import apiClient from '../config/axiosConfig';
import { API_BASE_URL } from '../utils/apiUtils';

const validateCardId = (cardId) => {
    if (!cardId || typeof cardId !== 'string' || cardId.trim() === '') {
        throw new Error('Card ID is required and must be a valid string');
    }

    const idRegex = /^[a-zA-Z0-9-_]+$/;
    if (!idRegex.test(cardId.trim())) {
        throw new Error('Card ID must contain only letters, numbers, hyphens, and underscores');
    }

    return cardId.trim();
};

const validatePassType = (passType) => {
    const validPassTypes = ['apple', 'google', 'universal'];
    if (passType && !validPassTypes.includes(passType.toLowerCase())) {
        throw new Error(`Invalid pass type. Valid types: ${validPassTypes.join(', ')}`);
    }
    return passType ? passType.toLowerCase() : 'universal';
};

const validateDeviceInfo = (deviceInfo) => {
    if (deviceInfo && typeof deviceInfo === 'object') {
        const validDeviceInfo = {};

        if (deviceInfo.platform) {
            if (typeof deviceInfo.platform !== 'string') {
                throw new Error('Device platform must be a string');
            }
            validDeviceInfo.platform = deviceInfo.platform.trim();
        }

        if (deviceInfo.userAgent) {
            if (typeof deviceInfo.userAgent !== 'string') {
                throw new Error('User agent must be a string');
            }
            validDeviceInfo.userAgent = deviceInfo.userAgent.trim();
        }

        if (deviceInfo.screenSize) {
            if (typeof deviceInfo.screenSize !== 'string') {
                throw new Error('Screen size must be a string');
            }
            validDeviceInfo.screenSize = deviceInfo.screenSize.trim();
        }

        return validDeviceInfo;
    }
    return {};
};

const validatePassOptions = (options = {}) => {
    const validOptions = {};

    if (options.passType !== undefined) {
        validOptions.passType = validatePassType(options.passType);
    }

    if (options.deviceInfo !== undefined) {
        validOptions.deviceInfo = validateDeviceInfo(options.deviceInfo);
    }

    if (options.forceRefresh !== undefined) {
        validOptions.forceRefresh = Boolean(options.forceRefresh);
    }

    return validOptions;
};

const handleUniversalWalletError = (error, operation = 'Universal wallet operation') => {
    if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        throw new Error('Your session has expired. Please log in again');
    }

    if (error.response?.status === 403) {
        throw new Error('You do not have permission to access universal wallet features');
    }

    if (error.response?.status === 404) {
        throw new Error('Card not found or universal wallet pass not available');
    }

    if (error.response?.status === 409) {
        throw new Error('Universal wallet pass already exists or conflicts with existing data');
    }

    if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.errors || [];
        const errorMessage = validationErrors.length > 0
            ? `Validation errors: ${validationErrors.join(', ')}`
            : 'Invalid universal wallet data provided';
        throw new Error(errorMessage);
    }

    if (error.response?.status === 503) {
        throw new Error('Universal wallet service temporarily unavailable. Please try again later.');
    }

    if (error.response?.status >= 500) {
        throw new Error('Universal wallet service error. Please try again later.');
    }

    if (error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection and try again.');
    }

    if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please try again.');
    }

    const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
    throw new Error(errorMessage);
};

export const getUniversalWalletPassUrl = (cardId) =>
    `${process.env.REACT_APP_API_BASE_URL}/api/universal-wallet/generate/${cardId}`;

export const generateUniversalWalletPass = async (cardId, options = {}) => {
    try {
        const validCardId = validateCardId(cardId);
        const validOptions = validatePassOptions(options);

        const response = await apiClient.post(
            `${API_BASE_URL}/api/universal-wallet/generate/${validCardId}`,
            validOptions
        );

        return response.data;
    } catch (error) {
        handleUniversalWalletError(error, 'Generating universal wallet pass');
    }
};

export const getUniversalWalletPassStatus = async (cardId) => {
    try {
        const validCardId = validateCardId(cardId);

        const response = await apiClient.get(
            `${API_BASE_URL}/api/universal-wallet/status/${validCardId}`
        );

        return response.data;
    } catch (error) {
        handleUniversalWalletError(error, 'Getting universal wallet pass status');
    }
};

export const updateUniversalWalletPass = async (cardId, updateData = {}) => {
    try {
        const validCardId = validateCardId(cardId);

        const validUpdateData = {};

        if (updateData.passType !== undefined) {
            validUpdateData.passType = validatePassType(updateData.passType);
        }

        if (updateData.deviceInfo !== undefined) {
            validUpdateData.deviceInfo = validateDeviceInfo(updateData.deviceInfo);
        }

        const response = await apiClient.put(
            `${API_BASE_URL}/api/universal-wallet/update/${validCardId}`,
            validUpdateData
        );

        return response.data;
    } catch (error) {
        handleUniversalWalletError(error, 'Updating universal wallet pass');
    }
};

export const deleteUniversalWalletPass = async (cardId) => {
    try {
        const validCardId = validateCardId(cardId);

        const response = await apiClient.delete(
            `${API_BASE_URL}/api/universal-wallet/delete/${validCardId}`
        );

        return response.data;
    } catch (error) {
        handleUniversalWalletError(error, 'Deleting universal wallet pass');
    }
};

export const getAllUniversalWalletPasses = async (page = 1, limit = 50) => {
    try {
        const validPage = page > 0 ? page : 1;
        const validLimit = limit > 0 && limit <= 100 ? limit : 50;

        const response = await apiClient.get(
            `${API_BASE_URL}/api/universal-wallet/passes?page=${validPage}&limit=${validLimit}`
        );

        return response.data;
    } catch (error) {
        handleUniversalWalletError(error, 'Getting all universal wallet passes');
    }
};

export const getDeviceCompatibility = async (deviceInfo = {}) => {
    try {
        const validDeviceInfo = validateDeviceInfo(deviceInfo);

        const response = await apiClient.post(
            `${API_BASE_URL}/api/universal-wallet/compatibility`,
            validDeviceInfo
        );

        return response.data;
    } catch (error) {
        handleUniversalWalletError(error, 'Getting device compatibility');
    }
};
